import React, { memo, Suspense, useContext, useEffect, useMemo, useState } from 'react';
import bem from 'utils/bem';
import Page from '../Page';
import DesktopCartItem from './CartItem';

import Text from 'components/Text';
import ShortItemTeaser, { ShortItemTeaserSkeleton } from 'components/ShopItemTeaser/ShortItemTeaser';
import { CartContext } from 'utils/cart';
import { useMobile } from 'utils/media';
import Model from '../../../model';
import { observer } from 'mobx-react-lite';
import { formatPrice } from 'utils/format';
import CheckoutForm from 'forms/forms/CheckoutForm';
import MobileCartItem, { MobileCartItemSkeleton } from './CartItem/MobileCartItem';
import MobileSlider from 'components/Slider/MobileSlider';
import './index.sass';
import { times } from 'utils/render';
import CartItem, { DesktopCartItemSkeleton } from './CartItem';
import { Skeleton } from '@mui/material';
import CheckoutFormSuccessModal from 'forms/forms/CheckoutForm/CheckoutFormSuccessModal';
import PickupInfoModal from 'forms/forms/CheckoutForm/PickupInfoModal';
import { proceedToCheckout } from '../../../model/utils';
import { IconButton } from 'components/Button';

const useCart = () => useContext( CartContext );

const NoItemsCart = () => {
    return (
        <>
            <div className={ el( 'no-items' ) }>
                <Text serif>В данный момент в корзине нет товаров</Text>
            </div>
            <TeasersBlock />
        </>
    )
}

const { block, el } = bem( 'cart' );

const Cart = ( {} ) => {
    const { cartItemsData } = Model.shop;

    useEffect(() => {
        proceedToCheckout();
    }, [])

    return (
        <Page className={ block() } title={ 'Корзина' }>
            <Suspense fallback={ <CartBlockSkeleton />}>
                <CartBlock resource={cartItemsData} />
            </Suspense>
        </Page>
    )
}

const CartBlock = observer(({ resource }) => {
    const shopItems = resource.read();
    const { isMobile } = useMobile();
    const [ cart ] = useCart();
    const [ total, setTotal ] = useState(0);
    const cartGoods = useMemo(() => {
        return cart.map(item => {
           const si = shopItems.find( g => g.data.id === item.id );
           if ( si ) {
               return { ...si, ...{ cartAmount: item.amount } }
           } else {
               return { id: item.id, cartAmount: item.amount, data: null };
           }
        })
    }, [shopItems, cart]);
    useEffect(() => {
        for (const cartItem of cart) {
            const cartGood = cartGoods.find(cg => cg.data?.id === cartItem.id);
            if (cartGood && (cartGood.cartAmount !== cartItem.amount)) {
                cartGood.cartAmount = cartItem.amount;
            }
        }
        const currTotal = cartGoods
            .filter( g => g.data && +g.data.amount > 0)
            .map( g => +g.data.price * +g.cartAmount )
            .reduce( ( acc, current ) => acc + current, 0 );
        setTotal(currTotal)
    }, [cart, cartGoods]);
    const totalIsNotEnough = total < 1300;
    const CartItem = isMobile ? MobileCartItem : DesktopCartItem;
    const checkoutCartGoods = cartGoods.filter(good => !!good.data);
    const { isKZNonAuthorized } = Model.kz;
    const onClickAuthorize = () => {
        proceedToCheckout();
    }

    return (
        <div className={ el( 'content' ) }>
            { cartGoods.length === 0 && <NoItemsCart /> }
            { cartGoods.length > 0 &&
                <>
                    <div className={ el('top')}>
                        <div className={ el( 'items' ) }>
                            { cartGoods.map( good => <CartItem key={good.id} item={ good }/> ) }
                        </div>
                        <div className={ el( 'summary' ) }>
                            <div className={el('summary-details')}>
                                <Text tag={'div'}>Заказ считается оформленным только после его оплаты, которая происходит сразу на сайте.</Text>
                                <Text tag={'div'}>Сохраненные в корзине товары не бронируются для покупки.</Text>
                            </div>
                            <div className={el('summary-price')}>
                                <div className={el('summary-price-top')}>
                                    <Text className={el('summary-price-label')}>Итого </Text>
                                    <Text className={el('summary-price-value')} serif>{ formatPrice(total) }</Text>
                                </div>
                                { totalIsNotEnough && <Text className={el('summary-price-label', 'minimum')}>{`* Минимальная сумма заказа ${ formatPrice(1300) }`}</Text> }
                            </div>
                        </div>
                        <TeasersBlock cartGoods={cartGoods} />
                    </div>
                    { !totalIsNotEnough && <div className={ el( 'checkout' ) }>
                        { isKZNonAuthorized && <div className={ el('authorize-msg')}>
                            <div className={el('authorize-msg-txt')}><Text>Пожалуйста, авторизуйтесь перед оформлением заказа</Text></div>
                            <div className={el('authorize-msg-btn')}><IconButton name={'arrow-right'} mod={'transparent-gold'} onClick={onClickAuthorize}><Text>Войти</Text></IconButton></div>
                        </div>
                        }
                        { !isKZNonAuthorized && <CheckoutForm price={total} items={checkoutCartGoods}/> }
                    </div> }
                </>
            }
            <PickupInfoModal />
            <CheckoutFormSuccessModal />
        </div>
    )
})

const CartBlockSkeleton = () => {
    const { isMobile } = useMobile();
    const CartItemSkeleton = isMobile ? MobileCartItemSkeleton : DesktopCartItemSkeleton;
    return (
        <div className={el('content')}>
            <div className={ el('top')}>
                <div className={ el( 'items' ) }>
                    { times((key) => <CartItemSkeleton key={key}  />, 1) }
                </div>
                <div className={ el( 'summary' ) }>
                    <div className={el('summary-details')}>
                        <Text tag={'div'}><Skeleton width={300} /></Text>
                        <Text tag={'div'}><Skeleton width={150}/></Text>
                    </div>
                    <div className={el('summary-price')}>
                        <div className={el('summary-price-top')}>
                            <Skeleton width={200}/>
                        </div>
                    </div>
                </div>
                <TeasersBlockSkeleton />
            </div>
        </div>
    )
}

const TeasersBlock = memo(({cartGoods}) => {
    const { isMobile } = useMobile();
    return (
        <div className={ el( 'teasers-block' ) }>
            <div className={ el( 'teasers-block-title' ) }>
                <Text serif>Вас может заинтересовать:</Text>
            </div>
            <div className={ el( 'teasers-block-list' ) }>
                <RelatedGoodsBlock cartGoods={cartGoods} isMobile={isMobile}/>
            </div>
        </div>
    )
})

const TeasersBlockSkeleton = () => {
    const { isMobile } = useMobile();
    const rRelatedGoods = times((key) => <ShortItemTeaserSkeleton key={ key } />, 3);
    const RelatedGoodsSkeleton = isMobile ? <MobileSlider items={rRelatedGoods} /> : rRelatedGoods
    return (
        <div className={ el( 'teasers-block' ) }>
            <div className={ el( 'teasers-block-title' ) }>
                <Text serif>Вас может заинтересовать:</Text>
            </div>
            <div className={ el( 'teasers-block-list' ) }>
                { RelatedGoodsSkeleton }
            </div>
        </div>
    )
}

const RelatedGoodsBlock = memo(({ cartGoods = [], isMobile}) => {
    const { getItemsSample } = Model.shop;
    const relatedGoods = useMemo(() => getItemsSample( cartGoods, 3 ), [cartGoods]);
    const rRelatedGoods = relatedGoods.map((item, index) => <ShortItemTeaser key={ index } item={ item }/>);
    return isMobile ? <MobileSlider items={rRelatedGoods} /> : rRelatedGoods;
})

export default observer(Cart);
